
import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { useAIReview } from '@/components/papers/upload/pdf/hooks/useAIReview';
import { adminLog, adminError } from '@/lib/utils/admin-logger';
import { deletePaper } from '@/lib/api/paper-operations/delete-paper-service';
import RemediationHeader from './remediation/RemediationHeader';
import RemediationTable from './remediation/RemediationTable';
import DuplicatePapersSection from './remediation/DuplicatePapersSection';
import DeletePaperDialog from '@/components/papers/DeletePaperDialog';

interface Paper {
  id: string;
  title: string;
  abstract: string;
  file_url: string;
  uploaded_at: string;
  keywords?: string[];
  needs_ai_review?: boolean;
  needs_keywords?: boolean;
  needs_research_info?: boolean;
  external_url?: string;
}

const RemediationOverview: React.FC = () => {
  const [papers, setPapers] = useState<Paper[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [processingPapers, setProcessingPapers] = useState<Set<string>>(new Set());
  const [processingKeywords, setProcessingKeywords] = useState<Set<string>>(new Set());
  const [processingResearchInfo, setProcessingResearchInfo] = useState<Set<string>>(new Set());
  const [deletingPapers, setDeletingPapers] = useState<Set<string>>(new Set());
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [paperToDelete, setPaperToDelete] = useState<Paper | null>(null);
  const { triggerAIReview } = useAIReview();

  const fetchPapersNeedingRemediation = async () => {
    setRefreshing(true);
    try {
      // Use the updated function that returns papers needing AI reviews, keywords, or research info
      const { data, error } = await supabase.rpc('get_papers_needing_remediation');
      
      if (error) {
        adminError('Error fetching papers needing remediation:', error);
        throw error;
      }
      
      adminLog(`Retrieved ${data?.length || 0} papers needing remediation`);
      setPapers(data as Paper[] || []);
      toast.success('Remediation data refreshed successfully');
    } catch (error) {
      console.error('Error fetching papers needing remediation:', error);
      toast.error('Failed to load remediation data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchPapersNeedingRemediation();
  }, []);

  const handleGenerateAIReview = async (paper: Paper) => {
    setProcessingPapers(prev => new Set(prev).add(paper.id));
    
    try {
      adminLog(`Initiating AI review for paper ${paper.id}: "${paper.title}"`);
      // Fetch current_version so we tie the review to the right paper version (avoids defaulting to v1.0).
      const { data: paperRow, error: paperRowError } = await supabase
        .from('papers')
        .select('current_version')
        .eq('id', paper.id)
        .single();
      if (paperRowError) throw paperRowError;
      const version = paperRow?.current_version || '1.0';
      await triggerAIReview(paper.file_url || paper.external_url || '', paper.id, paper.title, paper.abstract, true, version);
      toast.success(`AI review generation initiated for "${paper.title}"`);
      
      // Wait briefly before refreshing to allow process to start
      setTimeout(() => {
        fetchPapersNeedingRemediation();
      }, 2000);
    } catch (error) {
      adminError('Error generating AI review:', error);
      toast.error(`Failed to generate AI review for "${paper.title}"`);
    } finally {
      setProcessingPapers(prev => {
        const updated = new Set(prev);
        updated.delete(paper.id);
        return updated;
      });
    }
  };

  const handleGenerateKeywords = async (paper: Paper) => {
    setProcessingKeywords(prev => new Set(prev).add(paper.id));
    
    try {
      adminLog(`Initiating keyword generation for paper ${paper.id}: "${paper.title}"`);
      
      // Get external URL from the database
      const { data: paperData, error: fetchError } = await supabase
        .from('papers')
        .select('external_url')
        .eq('id', paper.id)
        .single();

      if (fetchError) {
        throw new Error(`Failed to fetch paper data: ${fetchError.message}`);
      }

      const response = await supabase.functions.invoke('extract-paper-data', {
        body: {
          paperId: paper.id,
          pdfUrl: paper.file_url,
          title: paper.title,
          abstract: paper.abstract,
          currentKeywords: paper.keywords || [],
          externalUrl: paperData?.external_url
        }
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      toast.success(`Keyword generation initiated for "${paper.title}"`);
      
      // Wait briefly before refreshing to allow process to start
      setTimeout(() => {
        fetchPapersNeedingRemediation();
      }, 2000);
    } catch (error) {
      adminError('Error generating keywords:', error);
      toast.error(`Failed to generate keywords for "${paper.title}"`);
    } finally {
      setProcessingKeywords(prev => {
        const updated = new Set(prev);
        updated.delete(paper.id);
        return updated;
      });
    }
  };

  const handleGenerateResearchInfo = async (paper: Paper) => {
    setProcessingResearchInfo(prev => new Set(prev).add(paper.id));
    
    try {
      adminLog(`Initiating research information extraction for paper ${paper.id}: "${paper.title}"`);
      
      // Get external URL from the database
      const { data: paperData, error: fetchError } = await supabase
        .from('papers')
        .select('external_url')
        .eq('id', paper.id)
        .single();

      if (fetchError) {
        throw new Error(`Failed to fetch paper data: ${fetchError.message}`);
      }

      const response = await supabase.functions.invoke('extract-paper-data', {
        body: {
          paperId: paper.id,
          pdfUrl: paper.file_url,
          title: paper.title,
          abstract: paper.abstract,
          currentKeywords: paper.keywords || [],
          externalUrl: paperData?.external_url
        }
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      toast.success(`Research information extraction initiated for "${paper.title}"`);
      
      // Wait briefly before refreshing to allow process to start
      setTimeout(() => {
        fetchPapersNeedingRemediation();
      }, 2000);
    } catch (error) {
      adminError('Error generating research information:', error);
      toast.error(`Failed to generate research information for "${paper.title}"`);
    } finally {
      setProcessingResearchInfo(prev => {
        const updated = new Set(prev);
        updated.delete(paper.id);
        return updated;
      });
    }
  };

  const handleDeletePaper = (paper: Paper) => {
    setPaperToDelete(paper);
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!paperToDelete) return;
    const id = paperToDelete.id;
    setDeletingPapers(prev => new Set(prev).add(id));
    setShowDeleteDialog(false);

    const success = await deletePaper(id);
    if (success) {
      setPapers(prev => prev.filter(p => p.id !== id));
    }

    setDeletingPapers(prev => {
      const updated = new Set(prev);
      updated.delete(id);
      return updated;
    });
    setPaperToDelete(null);
  };

  return (
    <div className="space-y-6">
      <RemediationHeader
        paperCount={papers.length}
        loading={loading}
        refreshing={refreshing}
        onRefresh={fetchPapersNeedingRemediation}
      />
      
      {/* Duplicate Papers Detection Section */}
      <DuplicatePapersSection />
      
      <RemediationTable
        papers={papers}
        loading={loading}
        processingPapers={processingPapers}
        processingKeywords={processingKeywords}
        processingResearchInfo={processingResearchInfo}
        deletingPapers={deletingPapers}
        onGenerateAIReview={handleGenerateAIReview}
        onGenerateKeywords={handleGenerateKeywords}
        onGenerateResearchInfo={handleGenerateResearchInfo}
        onDeletePaper={handleDeletePaper}
      />

      <DeletePaperDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        onConfirm={handleConfirmDelete}
        isDeleting={deletingPapers.size > 0}
      />
    </div>
  );
};

export default RemediationOverview;
